import React, { useState, useEffect } from "react";
import { ErrorMessage } from "formik";
import CustomButton from "../../../components/common/CustomButton/CustomButton";
import CustomTextInput from "../../../components/common/CustomTextInput";
import CustomSelect from "../../../components/common/CustomSelect";
import CustomSwitch from "../../../components/common/CustomSwitch";
import { UpdateInvoiceTemplateLogo } from "../../../utils/urls";

const ExtenderComponent = ({ title, children, isOpen, onToggle, onSaveNext, onCancel, isSectionValid }) => {
  return (
    <div className="border rounded-lg mb-2">
      <div 
        className="flex justify-between items-center p-3 cursor-pointer bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          {isSectionValid ? (
            <span className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </span>
          ) : (
            <span className="w-6 h-6 rounded-full bg-yellow-400 flex items-center justify-center text-white">
              i
            </span>
          )}
          <span className="text-gray-800 font-medium">{title}</span>
        </div>
        <svg 
          className={`w-5 h-5 transform ${isOpen ? 'rotate-180' : ''}`} 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </div>
      {isOpen && (
        <div className="p-4 bg-white">
          {children}
          <div className="flex justify-end gap-3 mt-4">
            <CustomButton
              type="button"
              onClick={onCancel}
              label="Cancel"
              variant="outlined"
            />
            <CustomButton
              type="button"
              onClick={onSaveNext}
              label="Save & Next"
              variant="contained"
              disabled={!isSectionValid} // Disable button if section is invalid
            />
          </div>
        </div>
      )}
    </div>
  );
};

const TemplateFormFields = ({ 
  expandedSection, 
  setExpandedSection, 
  values, 
  setFieldValue, 
  categories, 
  subCategories,
  templateId,
  isEditMode,
  isViewMode,
  formErrors,
  logoFile,
  setLogoFile,
}) => {
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState(null);
  const [logoPreview, setLogoPreview] = useState(values.upload_logo || null);
  const [phoneNumberError, setPhoneNumberError] = useState(""); // Local state for phone number error
  const [dateFormatError, setDateFormatError] = useState(""); // Local state for date format error

  useEffect(() => {
    // Update logoPreview when values.upload_logo changes (e.g., in edit mode)
    setLogoPreview(values.upload_logo || null);
  }, [values.upload_logo]);

  useEffect(() => {
    // Validate phone number only if it has a value
    setPhoneNumberError(values.phone_number ? validatePhoneNumber(values.phone_number) : "");
  }, [values.phone_number]);

  useEffect(() => {
    // Validate invoice date format only if it has a value
    setDateFormatError(values.invoice_date_format ? validateInvoiceDateFormat(values.invoice_date_format) : "");
  }, [values.invoice_date_format]);

  const handleSaveNext = (currentSection, nextSection) => {
    console.log(`Saving ${currentSection}...`);
    setExpandedSection(nextSection);
  };

  const handleCancel = (currentSection) => {
    console.log(`Canceling ${currentSection}...`);
    setExpandedSection(null);
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLogoFile(file); // Store the file for later upload
    const localPreviewUrl = URL.createObjectURL(file);
    setLogoPreview(localPreviewUrl);
    setFieldValue('upload_logo', localPreviewUrl); // Set local URL for form
    setLogoError(null);
  };

  const handleCategoryChange = (value) => {
    console.log('Category changed to:', value);
  };

  // Validation function for phone number (exactly 10 digits)
  const validatePhoneNumber = (value) => {
    const digitsOnly = value.replace(/[^0-9]/g, '');
    if (digitsOnly.length !== 10) return "Phone number must be exactly 10 digits";
    return "";
  };

  // Validation function for invoice date format (YYYY-MM-DD with valid values)
  const validateInvoiceDateFormat = (value) => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(value)) return "Please enter a valid date in YYYY-MM-DD format (e.g., 2024-12-10)";

    const [year, month, day] = value.split('-').map(Number);
    const currentYear = new Date().getFullYear();
    const isValidYear = year >= 1900 && year <= currentYear + 100; // Reasonable year range
    const isValidMonth = month >= 1 && month <= 12;
    const isValidDay = day >= 1 && day <= 31;

    if (!isValidYear || !isValidMonth || !isValidDay) {
      return "Please enter a valid date in YYYY-MM-DD format (e.g., 2024-12-10)";
    }

    // Additional check for days in month (e.g., February can't have 31 days)
    const date = new Date(year, month - 1, day);
    if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) {
      return "Invalid date in YYYY-MM-DD format (e.g., 2024-12-10)";
    }

    return "";
  };

  const availableColumns = [
    "Lead Name", "Lead ID", "Lead Email", "Lead Contact Number", 
    "Product Interest", "Product Category", "Product Tax", "Commission"
  ];

  const isSectionValid = (section) => {
    if (isViewMode) return true;

    switch (section) {
      case "companyHeader":
        return (
          values.company_name &&
          values.company_address &&
          values.phone_number &&
          values.email_address &&
          values.upload_logo && // Require logo for section to be valid
          !formErrors.company_name &&
          !formErrors.company_address &&
          !formErrors.email_address &&
          !phoneNumberError // Use local phone number error
        );
      case "invoiceInfo":
        return (
          values.invoice_title &&
          values.invoice_prefix &&
          values.invoice_date_format &&
          !formErrors.invoice_title &&
          !formErrors.invoice_prefix &&
          !dateFormatError // Use local date format error
        );
      case "billTo":
        return (
          values.customer_name !== undefined &&
          values.billing_address !== undefined &&
          values.phone_number_bill !== undefined &&
          values.email_address_bill !== undefined &&
          values.gstin_number_bill !== undefined
        );
      case "leadTable":
        return values.lead_table_preview_config.length > 0 && !formErrors.lead_table_preview_config;
      case "taxSummary":
        return (
          values.is_enable_taxes !== undefined &&
          (!values.cgst_enable || (values.cgst_value && !formErrors.cgst_value)) &&
          (!values.sgst_enable || (values.sgst_value && !formErrors.sgst_value)) &&
          (!values.igst_enable || (values.igst_value && !formErrors.igst_value))
        );
      case "totalsSection":
        return (
          values.is_show_subtotal !== undefined &&
          values.is_show_total_tax_amount !== undefined &&
          values.is_show_grand_total !== undefined
        );
      case "bankDetails":
        return (
          values.bank_name !== undefined &&
          values.account_number !== undefined &&
          values.ifsc_code !== undefined &&
          values.uip_id !== undefined
        );
      case "termsConditions":
        return (
          values.payment_terms_conditions &&
          values.thank_you_message &&
          !formErrors.payment_terms_conditions &&
          !formErrors.thank_you_message
        );
      default:
        return false;
    }
  };

  return (
    <>
      <div className="mb-4">
        <CustomTextInput
          name="title"
          label="Template Name"
          placeholder="Enter template name"
          className="w-full"
          disabled={isViewMode}
        />
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div>
          <CustomSelect
            label="Category"
            placeholder={values?.category_title || "Select Category"}
            name="category_title"
            menuList={categories.map((cat) => ({
              value: cat?.category_id,
              label: cat?.title,
            }))}
            onChange={(e) => {
              const selectedCategory = categories.find(
                (cat) => cat.category_id === e.target.value
              );
              handleCategoryChange(e.target.value);
              setFieldValue("category_id", e.target.value);
              setFieldValue("category_title", selectedCategory?.title || "");
              setFieldValue("sub_category_id", "");
              setFieldValue("sub_category_title", "");
            }}
            className="w-full"
            disabled={isViewMode}
          />
          <ErrorMessage name="category_title" component="div" className="text-red-500 text-sm mt-1" />
        </div>
        <div>
          <CustomSelect
            label="Sub Category"
            placeholder={values?.sub_category_title || "Select Sub Category"}
            name="sub_category_title"
            menuList={subCategories.map((sub) => ({
              value: sub?.sub_category_id,
              label: sub?.title,
            }))}
            onChange={(e) => {
              const selectedSubCategory = subCategories.find(
                (sub) => sub.sub_category_id === e.target.value
              );
              setFieldValue("sub_category_id", e.target.value);
              setFieldValue("sub_category_title", selectedSubCategory?.title || "");
            }}
            className="w-full"
            disabled={isViewMode}
          />
          <ErrorMessage name="sub_category_title" component="div" className="text-red-500 text-sm mt-1" />
        </div>
      </div>

      <ExtenderComponent
        title="Company Header"
        isOpen={expandedSection === "companyHeader"}
        onToggle={() => setExpandedSection(expandedSection === "companyHeader" ? null : "companyHeader")}
        onSaveNext={() => handleSaveNext("companyHeader", "invoiceInfo")}
        onCancel={() => handleCancel("companyHeader")}
        isSectionValid={isSectionValid("companyHeader")}
      >
        <div className="grid grid-cols-3 gap-4">
          <div>
            <CustomTextInput name="company_name" label="Company Name" placeholder="Enter company name" disabled={isViewMode} />
          </div>
          <div>
            <CustomTextInput name="company_address" label="Company Address" placeholder="Enter company address" disabled={isViewMode} />
          </div>
          <div>
            <CustomTextInput 
              name="phone_number" 
              label="Phone Number" 
              placeholder="Enter phone number" 
              disabled={isViewMode}
              type="text"
              onChange={(e) => {
                const value = e.target.value.replace(/[^0-9]/g, ''); // Allow only digits
                setFieldValue("phone_number", value);
                const error = value ? validatePhoneNumber(value) : "";
                setPhoneNumberError(error);
              }}
            />
            {phoneNumberError && (
              <div className="text-red-500 text-sm mt-1">{phoneNumberError}</div>
            )}
           
          </div>
          <div>
            <CustomTextInput name="email_address" label="Email Address" placeholder="Enter email address" disabled={isViewMode} />
            {/* <ErrorMessage name="email_address" component="div" className="text-red-500 text-sm mt-1" /> */}
          </div>
          <div>
            <CustomTextInput name="gstin_number" label="GSTIN Number" placeholder="Enter GSTIN number" disabled={isViewMode} />
            <ErrorMessage name="gstin_number" component="div" className="text-red-500 text-sm mt-1" />
          </div>
          <div className="flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Upload Logo</label>
            <div className="flex items-center gap-3">
              {logoPreview ? (
                <div className="relative">
                  <img 
                    src={logoPreview} 
                    alt="Logo Preview" 
                    className="w-24 h-24 object-contain rounded-lg border" 
                  />
                  {!isViewMode && (
                    <button
                      onClick={() => {
                        setFieldValue('upload_logo', '');
                        setLogoPreview(null);
                        setLogoFile(null); // Clear logo file
                      }}
                      className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center"
                      disabled={logoUploading}
                    >
                      ×
                    </button>
                  )}
                </div>
              ) : (
                <div className="w-24 h-24 flex items-center justify-center bg-gray-100 rounded-lg border">
                  <span className="text-gray-500">No Logo</span>
                </div>
              )}
              {!isViewMode && (
                <label className="px-4 py-2 bg-blue-500 text-white rounded-lg cursor-pointer hover:bg-blue-600 transition">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    disabled={logoUploading}
                  />
                  {logoUploading ? "Uploading..." : "Upload Logo"}
                </label>
              )}
            </div>
            {logoError && <p className="text-red-500 text-sm mt-1">{logoError}</p>}
          </div>
        </div>
      </ExtenderComponent>

      <ExtenderComponent
        title="Invoice Info"
        isOpen={expandedSection === "invoiceInfo"}
        onToggle={() => setExpandedSection(expandedSection === "invoiceInfo" ? null : "invoiceInfo")}
        onSaveNext={() => handleSaveNext("invoiceInfo", "billTo")}
        onCancel={() => handleCancel("invoiceInfo")}
        isSectionValid={isSectionValid("invoiceInfo")}
      >
        <div className="grid grid-cols-3 gap-4">
          <div>
            <CustomTextInput name="invoice_title" label="Invoice Title" placeholder="Enter invoice title" disabled={isViewMode} />
          </div>
          <div>
            <CustomTextInput name="invoice_prefix" label="Invoice Prefix" placeholder="Enter invoice prefix" disabled={isViewMode} />
          </div>
          <div>
            <CustomTextInput 
              name="invoice_date_format" 
              label="Invoice Date Format" 
              placeholder="YYYY-MM-DD" 
              disabled={isViewMode}
              onChange={(e) => {
                const value = e.target.value;
                setFieldValue("invoice_date_format", value);
                const error = value ? validateInvoiceDateFormat(value) : "";
                setDateFormatError(error);
              }}
            />
            {dateFormatError && (
              <div className="text-red-500 text-sm mt-1">{dateFormatError}</div>
            )}
            {/* <ErrorMessage name="invoice_date_format" component="div" className="text-red-500 text-sm mt-1" /> */}
          </div>
        </div>
      </ExtenderComponent>

      <ExtenderComponent
        title="Bill To"
        isOpen={expandedSection === "billTo"}
        onToggle={() => setExpandedSection(expandedSection === "billTo" ? null : "billTo")}
        onSaveNext={() => handleSaveNext("billTo", "leadTable")}
        onCancel={() => handleCancel("billTo")}
        isSectionValid={isSectionValid("billTo")}
      >
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <span>Customer Name</span>
            <CustomSwitch
              name="customer_name"
              value={values.customer_name}
              onChange={() => setFieldValue("customer_name", !values.customer_name)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>Billing Address</span>
            <CustomSwitch
              name="billing_address"
              value={values.billing_address}
              onChange={() => setFieldValue("billing_address", !values.billing_address)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>Phone Number</span>
            <CustomSwitch
              name="phone_number_bill"
              value={values.phone_number_bill}
              onChange={() => setFieldValue("phone_number_bill", !values.phone_number_bill)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>Email Address</span>
            <CustomSwitch
              name="email_address_bill"
              value={values.email_address_bill}
              onChange={() => setFieldValue("email_address_bill", !values.email_address_bill)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>GSTIN Number</span>
            <CustomSwitch
              name="gstin_number_bill"
              value={values.gstin_number_bill}
              onChange={() => setFieldValue("gstin_number_bill", !values.gstin_number_bill)}
              disabled={isViewMode}
            />
          </div>
        </div>
      </ExtenderComponent>

      <ExtenderComponent
        title="Lead Table Preview"
        isOpen={expandedSection === "leadTable"}
        onToggle={() => setExpandedSection(expandedSection === "leadTable" ? null : "leadTable")}
        onSaveNext={() => handleSaveNext("leadTable", "taxSummary")}
        onCancel={() => handleCancel("leadTable")}
        isSectionValid={isSectionValid("leadTable")}
      >
        <div className="mb-4">
          <h3 className="text-sm font-medium mb-2">Column Configuration</h3>
          <div className="border rounded-lg p-2">
            <CustomSelect
              label="Select Columns"
              name="lead_table_preview_config"
              menuList={availableColumns.map(col => ({ label: col, value: col }))}
              value={values.lead_table_preview_config}
              onChange={(e) => {
                const selectedColumns = Array.isArray(e.target.value) ? e.target.value : [e.target.value];
                setFieldValue("lead_table_preview_config", selectedColumns);
              }}
              multiple
              className="w-full"
              disabled={isViewMode}
            />
            <ErrorMessage name="lead_table_preview_config" component="div" className="text-red-500 text-sm mt-1" />
          </div>
        </div>
      </ExtenderComponent>

      <ExtenderComponent
        title="Tax Summary"
        isOpen={expandedSection === "taxSummary"}
        onToggle={() => setExpandedSection(expandedSection === "taxSummary" ? null : "taxSummary")}
        onSaveNext={() => handleSaveNext("taxSummary", "totalsSection")}
        onCancel={() => handleCancel("taxSummary")}
        isSectionValid={isSectionValid("taxSummary")}
      >
        <div className="flex items-center gap-4 mb-4">
          <span>Enable Taxes</span>
          <CustomSwitch
            name="is_enable_taxes"
            value={values.is_enable_taxes}
            onChange={() => setFieldValue("is_enable_taxes", !values.is_enable_taxes)}
            disabled={isViewMode}
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="flex items-center gap-2">
              <span>CGST [%]</span>
              <CustomSwitch
                name="cgst_enable"
                value={values.cgst_enable}
                onChange={() => setFieldValue("cgst_enable", !values.cgst_enable)}
                disabled={isViewMode}
              />
            </div>
            {values.cgst_enable && (
              <div>
                <CustomTextInput
                  name="cgst_value"
                  label="CGST Value"
                  placeholder="Enter CGST %"
                  type="number"
                  disabled={isViewMode}
                />
                <ErrorMessage name="cgst_value" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span>SGST [%]</span>
              <CustomSwitch
                name="sgst_enable"
                value={values.sgst_enable}
                onChange={() => setFieldValue("sgst_enable", !values.sgst_enable)}
                disabled={isViewMode}
              />
            </div>
            {values.sgst_enable && (
              <div>
                <CustomTextInput
                  name="sgst_value"
                  label="SGST Value"
                  placeholder="Enter SGST %"
                  type="number"
                  disabled={isViewMode}
                />
                <ErrorMessage name="sgst_value" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span>IGST [%]</span>
              <CustomSwitch
                name="igst_enable"
                value={values.igst_enable}
                onChange={() => setFieldValue("igst_enable", !values.igst_enable)}
                disabled={isViewMode}
              />
            </div>
            {values.igst_enable && (
              <div>
                <CustomTextInput
                  name="igst_value"
                  label="IGST Value"
                  placeholder="Enter IGST %"
                  type="number"
                  disabled={isViewMode}
                />
                <ErrorMessage name="igst_value" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            )}
          </div>
        </div>
      </ExtenderComponent>

      <ExtenderComponent
        title="Totals Section"
        isOpen={expandedSection === "totalsSection"}
        onToggle={() => setExpandedSection(expandedSection === "totalsSection" ? null : "totalsSection")}
        onSaveNext={() => handleSaveNext("totalsSection", "bankDetails")}
        onCancel={() => handleCancel("totalsSection")}
        isSectionValid={isSectionValid("totalsSection")}
      >
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <span>Subtotal</span>
            <CustomSwitch
              name="is_show_subtotal"
              value={values.is_show_subtotal}
              onChange={() => setFieldValue("is_show_subtotal", !values.is_show_subtotal)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>Total Tax Amount</span>
            <CustomSwitch
              name="is_show_total_tax_amount"
              value={values.is_show_total_tax_amount}
              onChange={() => setFieldValue("is_show_total_tax_amount", !values.is_show_total_tax_amount)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>Grand Total</span>
            <CustomSwitch
              name="is_show_grand_total"
              value={values.is_show_grand_total}
              onChange={() => setFieldValue("is_show_grand_total", !values.is_show_grand_total)}
              disabled={isViewMode}
            />
          </div>
        </div>
      </ExtenderComponent>

      <ExtenderComponent
        title="Bank Details"
        isOpen={expandedSection === "bankDetails"}
        onToggle={() => setExpandedSection(expandedSection === "bankDetails" ? null : "bankDetails")}
        onSaveNext={() => handleSaveNext("bankDetails", "termsConditions")}
        onCancel={() => handleCancel("bankDetails")}
        isSectionValid={isSectionValid("bankDetails")}
      >
        <div className="grid grid-cols-3 gap-4">
          <div className="flex items-center gap-2">
            <span>Bank Name</span>
            <CustomSwitch
              name="bank_name"
              value={values.bank_name}
              onChange={() => setFieldValue("bank_name", !values.bank_name)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>Account Number</span>
            <CustomSwitch
              name="account_number"
              value={values.account_number}
              onChange={() => setFieldValue("account_number", !values.account_number)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>IFSC Code</span>
            <CustomSwitch
              name="ifsc_code"
              value={values.ifsc_code}
              onChange={() => setFieldValue("ifsc_code", !values.ifsc_code)}
              disabled={isViewMode}
            />
          </div>
          <div className="flex items-center gap-2">
            <span>UPI ID</span>
            <CustomSwitch
              name="uip_id"
              value={values.uip_id}
              onChange={() => setFieldValue("uip_id", !values.uip_id)}
              disabled={isViewMode}
            />
          </div>
        </div>
      </ExtenderComponent>

      <ExtenderComponent
        title="Terms and Conditions"
        isOpen={expandedSection === "termsConditions"}
        onToggle={() => setExpandedSection(expandedSection === "termsConditions" ? null : "termsConditions")}
        onSaveNext={() => handleSaveNext("termsConditions", null)}
        onCancel={() => handleCancel("termsConditions")}
        isSectionValid={isSectionValid("termsConditions")}
      >
        <div className="grid grid-cols-2 gap-4">
          <div>
            <CustomTextInput name="payment_terms_conditions" label="Payment Terms" placeholder="Payment to be made" disabled={isViewMode} />
            <ErrorMessage name="payment_terms_conditions" component="div" className="text-red-500 text-sm mt-1" />
          </div>
          <div>
            <CustomTextInput name="thank_you_message" label="Thank you Message" placeholder="Thank you for your business" disabled={isViewMode} />
            <ErrorMessage name="thank_you_message" component="div" className="text-red-500 text-sm mt-1" />
          </div>
        </div>
      </ExtenderComponent>
    </>
  );
};

export default TemplateFormFields;